/**
 * The contract between the admin host page and the impersonation frame.
 *
 * The host (the admin area) owns the impersonation session: who is being
 * impersonated and the short-lived actor token that makes platform requests
 * resolve as them. The frame (the member portal loaded in an iframe) never
 * mints or stores a credential of its own — it asks the host for the current
 * session on boot and again whenever its copy of the token is about to expire.
 *
 * Both sides talk over `postMessage` on the same origin. Every inbound message
 * is `unknown` until it parses (coding.md Rule 19/20): a message that fails
 * the schema is ignored, never acted on.
 */

import { z } from "zod";

/**
 * The `name` the host gives the iframe. `window.name` persists across every
 * navigation inside the frame, so the portal can tell it is embedded for
 * impersonation without a URL marker that route validation could strip.
 */
export const IMPERSONATION_FRAME_NAME = "birdwatch-impersonation-frame";

export const impersonationTargetSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  userEmail: z.string(),
  accountId: z.string(),
  accountName: z.string().nullable(),
  placeId: z.string().nullable(),
  placeLabel: z.string().nullable(),
});

export type ImpersonationTarget = z.infer<typeof impersonationTargetSchema>;

export const impersonationSessionSchema = z.object({
  /** Platform actor token; every request in the frame carries it as the bearer. */
  token: z.string(),
  /** Absolute expiry of `token`, ISO 8601. */
  expiresAt: z.iso.datetime(),
  target: impersonationTargetSchema,
});

export type ImpersonationSession = z.infer<typeof impersonationSessionSchema>;

/** Messages the frame sends to the host. */
export const frameMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("impersonation:request-session") }),
  z.object({ type: z.literal("impersonation:exit") }),
]);

export type FrameMessage = z.infer<typeof frameMessageSchema>;

/** Messages the host sends to the frame. */
export const hostMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("impersonation:session"),
    session: impersonationSessionSchema,
  }),
  z.object({
    type: z.literal("impersonation:session-error"),
    message: z.string(),
  }),
]);

export type HostMessage = z.infer<typeof hostMessageSchema>;

/** Parse a `postMessage` payload from the frame; `null` for anything else. */
export function parseFrameMessage(data: unknown): FrameMessage | null {
  const result = frameMessageSchema.safeParse(data);
  return result.success ? result.data : null;
}

/** Parse a `postMessage` payload from the host; `null` for anything else. */
export function parseHostMessage(data: unknown): HostMessage | null {
  const result = hostMessageSchema.safeParse(data);
  return result.success ? result.data : null;
}
